import { useState, useEffect } from "react";

import { useTranslation } from "react-i18next";
import ReactLoading from "react-loading";
import { useNavigate, useLocation } from "react-router-dom";
import VerificationInput from "react-verification-input";

import { useToast } from "hooks/toast";

import { LanguageSelector } from "components/LanguageSelector";

import { HearingInviteRoomService } from "services/hearingInviteRoom";

import kentaLogo from "assets/KENTA_4.0.png";

import {
  Container,
  Content,
  LogoContainer,
  HearingInfoContainer,
  HearingInfoText,
  Description,
  VerificationWrapper,
  ButtonWrapper,
  Button,
  ErrorText,
  Title,
  LanguageSelectorWrapper,
} from "./styles";

interface HearingInfo {
  customerName: string;
  procedureNumber: string;
  expectedDate: string;
  attendantName: string;
  document: string;
  documentLenght: number;
}

const VerificationCode = () => {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [finalError, setFinalError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [hearingInfo, setHearingInfo] = useState<HearingInfo | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();

  useEffect(() => {
    const fetchHearingInfo = async () => {
      const queryParams = new URLSearchParams(location.search);
      const token = queryParams.get("token");

      if (!token) {
        setFinalError(t("verification.invalidToken"));
        setIsInitialLoading(false);
        return;
      }

      try {
        const response = await HearingInviteRoomService.getHearingInfo(token);

        if (response.success) {
          setHearingInfo(response.data);

          const attendantNameWithRole = response.data.attendantName;
          const attendantNameSplit = attendantNameWithRole.split(":");
          const attendantName = attendantNameSplit[1] || attendantNameWithRole;
          addToast({
            type: "warning",
            title: t("verification.attentionTitle"),
            description: t("audiences.messages.recordingConsent", {
              name: attendantName,
            }),
            timeLimitToast: 5000,
          });
        } else {
          setFinalError(response.message);
        }
      } catch (err: any) {
        setFinalError(t("audiences.messages.fetchError"));
      } finally {
        setIsInitialLoading(false);
      }
    };

    fetchHearingInfo();
  }, [location.search, navigate, addToast, t]);

  const processCodeInput = (inputCode: string): string => {
    if (!hearingInfo?.document) {
      return inputCode;
    }

    const prefix = hearingInfo.document;

    if (inputCode.length <= prefix.length) {
      return prefix;
    }

    const prefixFromInput = inputCode.substring(0, prefix.length);
    if (prefixFromInput !== prefix) {
      const additionalDigits = inputCode.substring(
        Math.max(0, inputCode.length - (inputCode.length - prefix.length)),
      );
      return prefix + additionalDigits;
    }

    return inputCode;
  };

  const handleCodeChange = (newCode: string) => {
    setCode(processCodeInput(newCode));
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();

    const clipboardData = event.clipboardData;
    let pastedText = clipboardData.getData("text");

    pastedText = pastedText.replace(/[^0-9]/g, "");

    if (!pastedText) return;

    if (
      pastedText.startsWith(code) &&
      pastedText.length === hearingInfo?.documentLenght
    ) {
      setCode(pastedText);
    } else {
      setCode(code + pastedText);
    }

    setTimeout(() => {
      const inputElements = document.querySelectorAll(
        ".verification-character",
      );
      if (inputElements.length > 0) {
        const lastIndex = Math.min(code.length, inputElements.length - 1);
        (inputElements[lastIndex] as HTMLElement).focus();
      }
    }, 0);
  };

  const handleValidate = async () => {
    if (!hearingInfo) return;

    if (code.length !== hearingInfo.documentLenght) {
      setError(t("verification.enterFullDocument"));
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const queryParams = new URLSearchParams(location.search);
      const token = queryParams.get("token");

      const response = await HearingInviteRoomService.checkDocument({
        token: token,
        document: code,
      });

      if (!response.success) {
        const { message } = response;
        setError(message.detail || t("verification.documentValidationError"));
        return;
      }

      window.location.href = response.data;
    } catch (err: any) {
      setError(t("verification.documentValidationError"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (hearingInfo && hearingInfo.document && code === "") {
      setCode(hearingInfo.document);
    }
  }, [hearingInfo, code]);

  if (isInitialLoading) {
    return (
      <Container>
        <Content>
          <LogoContainer>
            <img
              src={kentaLogo}
              alt="Logo da Empresa"
              width={240}
              height={90}
            />
          </LogoContainer>
          <ReactLoading type="spin" color="#F2A60D" width={24} height={24} />
        </Content>
      </Container>
    );
  }

  if (finalError) {
    return (
      <Container>
        <Content>
          <LogoContainer>
            <img
              src={kentaLogo}
              alt="Logo da Empresa"
              width={240}
              height={90}
            />
          </LogoContainer>
          <Title>{t("verification.attentionTitle")}</Title>
          <p>{finalError}</p>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <LanguageSelectorWrapper>
        <LanguageSelector />
      </LanguageSelectorWrapper>
      <Content>
        <LogoContainer>
          <img src={kentaLogo} alt="Logo da Empresa" width={240} height={90} />
        </LogoContainer>

        {hearingInfo && (
          <HearingInfoContainer>
            <HearingInfoText>{hearingInfo.customerName}</HearingInfoText>
            <HearingInfoText>{hearingInfo.procedureNumber}</HearingInfoText>
            <HearingInfoText>{hearingInfo.expectedDate}</HearingInfoText>
            <HearingInfoText>{hearingInfo.attendantName}</HearingInfoText>
          </HearingInfoContainer>
        )}

        <Description>{t("verification.enterRemainingDigits")}</Description>

        <VerificationWrapper>
          <div onPaste={handlePaste}>
            <VerificationInput
              value={code}
              onChange={handleCodeChange}
              length={hearingInfo?.documentLenght || 11}
              placeholder="."
              autoFocus
              validChars="0-9"
              classNames={{
                container: "verification-container",
                character: "verification-character",
                characterInactive: "verification-character--inactive",
                characterSelected: "verification-character--selected",
                characterFilled: "verification-character--filled",
              }}
            />
          </div>
        </VerificationWrapper>

        {error && <ErrorText>{error || error}</ErrorText>}

        <ButtonWrapper>
          <Button onClick={handleValidate} disabled={isLoading}>
            {isLoading
              ? t("verification.validating")
              : t("verification.validate")}
          </Button>
        </ButtonWrapper>
      </Content>
    </Container>
  );
};

export default VerificationCode;
